import { Alert, Divider, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Box } from '@mui/system';
import * as Styles from '../../../assets/styles/appStyles';
import * as ListStyles from '../../Locations/Styles/ListCustodiansStyles';
import CustodianDetailsViewModel from '../../../Models/custodian/custodian-details-viewmodel';
import { horizontalLine } from '../../../assets/styles/appStyles';
import {
  gridContainer,
  infoGridItem,
} from '../Styles/CustodianBasicDetailsStyles';
import CustodianAddressDetails from './CustodianAddressDetails';
import { Link } from 'react-router-dom';
import useAuth from '../../../hooks/context/useAuth';
import { appActions } from '../../../settings/appConfig';

export default function CustodianBasicDetails({
  custodian,
}: Readonly<{
  custodian: CustodianDetailsViewModel;
}>) {
  const { isAuthorized } = useAuth();
  return (
    <Grid rowSpacing={3} container sx={gridContainer} spacing={2}>
      <Grid container item rowSpacing={3}>
        <Grid item xs={4} sx={infoGridItem}>
          <Typography align="left" sx={Styles.labelTitle}>
            Entity Name
          </Typography>
          <Box>
            <Typography sx={Styles.labelText}>{custodian?.name}</Typography>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={horizontalLine} />
      <Grid item xs={12} sx={infoGridItem}>
        <Typography align="left" sx={Styles.labelTitle}>
          Entity Type : {custodian?.isFacility && "Facility"}{(custodian?.isFacility && custodian?.isCustodian) && " / "}{custodian?.isCustodian && "Custodian"}
        </Typography>

        <Box
          display="flex"
          justifyContent="space-between"
          gap={4}
          mt={2}
          flexWrap="wrap"
        >
          {custodian?.isFacility && (
            <Box flex={1} minWidth="300px">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Box sx={Styles.entityTypeText}>Facility</Box>
                <Box sx={Styles.entityCountIndicator}>
                  {"→ " + custodian?.facilityCount}
                </Box>
              </Box>

              {custodian?.facilityAssociations?.length > 0 && (
                <TableContainer
                  component={Paper}
                  sx={{
                    border: "0.5px chocolate #e0e0e0",
                    borderRadius: 1,
                    boxShadow: "1",
                  }}
                >
                  <Table size="small" aria-label="facility table">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Associated Custodians</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Entity Status</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {custodian.facilityAssociations.map((f) => (
                        <TableRow
                          key={f.entityId}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "#fafafa" },
                          }}
                        >
                          <TableCell>
                            <Link
                              to={`/entity/details/${f.entityId}`}
                              target="_blank"
                              style={ListStyles.default.viewCustodianLinkStyles}
                            >
                              {f.name}
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              color: f.disabled ? "red" : "green",
                              fontWeight: 500,
                            }}
                          >
                            {f.disabled ? "Inactive" : "Active"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {custodian?.isCustodian && (
            <Box flex={1} minWidth="300px">
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Box sx={Styles.entityTypeText}>Custodian</Box>
                <Box sx={Styles.entityCountIndicator}>
                  {"← " + custodian?.custodianCount}
                </Box>
              </Box>

              {custodian?.custodianAssociations?.length > 0 && (
                <TableContainer
                  component={Paper}
                  sx={{
                    border: "0.5px initial #e0e0e0",
                    borderRadius: 1,
                    boxShadow: "1",
                  }}
                >
                  <Table size="small" aria-label="custodian table">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>Associated Facilities</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Entity Status</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {custodian.custodianAssociations.map((c) => (
                        <TableRow
                          key={c.entityId}
                          hover
                          sx={{
                            "&:hover": { backgroundColor: "#fafafa" },
                          }}
                        >
                          <TableCell>
                            <Link
                              to={`/entity/details/${c.entityId}`}
                              target="_blank"
                              style={ListStyles.default.viewCustodianLinkStyles}
                            >
                              {c.name}
                            </Link>
                          </TableCell>
                          <TableCell
                            sx={{
                              color: c.disabled ? "red" : "green",
                              fontWeight: 500,
                            }}
                          >
                            {c.disabled ? "Inactive" : "Active"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

        </Box>
        {custodian?.isFacility && isAuthorized(appActions.editAssociations) &&
          custodian?.facilityAssociations?.length > 0 &&
          <Grid item xs={12} sx={infoGridItem}>
            <Link
              to={`/associations/edit/${custodian.id}`}
              target="_blank"
              style={ListStyles.default.viewCustodianLinkStyles}
            >
              Manage Associations
            </Link>
          </Grid>}

        {custodian?.isFacility &&
          (isAuthorized(appActions.editAssociations) || isAuthorized(appActions.addAssociations)) &&
          custodian?.facilityAssociations?.length === 0 && (
            <Grid item xs={12} sx={infoGridItem}>
              <Link
                to={`/associations/add/${custodian.id}`}
                target="_blank"
                style={ListStyles.default.viewCustodianLinkStyles}
              >
                Manage Associations
              </Link>

              <Box sx={{ marginTop: 1 }}></Box>

              <Alert
                severity="warning"
                sx={{
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  margin: 2,
                  padding: 2,
                  width: '96%',
                }}
              >
                <strong>There are no custodians associated with this facility.</strong>
                <div style={{ marginLeft: '10px', fontSize: '0.9rem' }}>
                  Please try managing associations to add new associations.
                </div>
              </Alert>
            </Grid>
          )}
      </Grid>
      <Divider sx={horizontalLine} />
      <Grid container item rowSpacing={3}>
        <CustodianAddressDetails
          address={custodian.address}
        ></CustodianAddressDetails>
      </Grid>

      <Divider sx={horizontalLine} />
      {custodian.phoneNumbers?.map((phoneNumber) => (
        <Grid container item rowSpacing={3} key={phoneNumber.id}>
          <Grid item xs={4} sx={infoGridItem}>
            <Typography align="left" sx={Styles.labelTitle}>
              Fax/Phone number
            </Typography>
            <Box>
              <Typography sx={Styles.labelText}>
                {phoneNumber.phoneNumber}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4} sx={infoGridItem}>
            <Typography align="left" sx={Styles.labelTitle}>
              Number Type
            </Typography>
            <Box>
              <Typography sx={Styles.labelText}>
                {phoneNumber.phoneType}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ))}
      <Divider sx={horizontalLine} />
      {custodian.portalUrls?.map((portalUrl) => (
        <Grid container item rowSpacing={3} key={portalUrl.id}>
          <Grid item xs={4} sx={infoGridItem}>
            <Typography align="left" sx={Styles.labelTitle}>
              Portal URL
            </Typography>
            <Box>
              <Typography sx={Styles.labelText}>
                {portalUrl.portalUrl}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4} sx={infoGridItem}>
            <Typography align="left" sx={Styles.labelTitle}>
              URL Type
            </Typography>
            <Box>
              <Typography sx={Styles.labelText}>
                {portalUrl.portalType}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4} sx={infoGridItem}>
            <Typography align="left" sx={Styles.labelTitle}>
              Portal Notes
            </Typography>
            <Box>
              <Typography sx={Styles.labelText}>{portalUrl.portalNotes ? portalUrl.portalNotes : "-"}</Typography>
            </Box>
          </Grid>
        </Grid>
      ))}
      {custodian?.portalUrls?.length > 0 && <Divider sx={horizontalLine} />}

      {custodian?.emailAddresses?.map((email) => (
        <Grid container item rowSpacing={3} key={email.id}>
          <Grid item xs={4} sx={infoGridItem}>
            <Typography align="left" sx={Styles.labelTitle}>
              Email Address
            </Typography>
            <Box>
              <Typography sx={Styles.labelText}>{email.email}</Typography>
            </Box>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}
